import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { api, ApiError, type PlayerProfile, type TournamentDivision } from "../lib/api";

interface EnrollExistingPlayersDialogProps {
  token: string;
  tournamentId: number;
  divisions: TournamentDivision[];
  /** Ya se inscribió al menos uno: la pantalla recarga sus datos. */
  onEnrolled: (message: string) => void;
  onClose: () => void;
}

/**
 * «Inscribir jugadores existentes» (paso 5 Inscripciones, 2026-10-11): los
 * jugadores que ya están en la base del sistema, con búsqueda y selección
 * múltiple, a la categoría elegida. Mismo camino que «Agregar jugadores
 * existentes» de Jugadores del torneo (api.participants.bulkEnroll): el
 * servidor salta a los que ya estaban inscritos en esa categoría.
 */
export default function EnrollExistingPlayersDialog({
  token,
  tournamentId,
  divisions,
  onEnrolled,
  onClose,
}: EnrollExistingPlayersDialogProps) {
  const ids = useId();
  const activeDivisions = divisions.filter((division) => division.is_active !== false);
  const [divisionId, setDivisionId] = useState<string>(
    activeDivisions.length === 1 ? String(activeDivisions[0].id) : "",
  );
  const [search, setSearch] = useState("");
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const onCloseRef = useRef(onClose);
  const submittingRef = useRef(submitting);
  useEffect(() => {
    onCloseRef.current = onClose;
    submittingRef.current = submitting;
  });

  const divisionRequired = activeDivisions.length > 0;

  // Foco en la búsqueda al abrir; Escape cierra; al cerrar, el foco regresa.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    searchRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape" || submittingRef.current) return;
      event.stopPropagation();
      onCloseRef.current();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus?.();
    };
  }, []);

  // Búsqueda mientras se teclea (mismo endpoint que Jugadores del torneo).
  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setSearching(true);
      api.players
        .search(token, { search: search.trim() || undefined, limit: 50 })
        .then((response) => {
          if (!cancelled) setPlayers(response.data.players);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : "No pudimos cargar los jugadores.");
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [token, search]);

  function toggle(playerId: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  }

  async function handleSubmit() {
    if (selected.size === 0) return;
    if (divisionRequired && !divisionId) {
      setError("Elige la categoría donde se van a inscribir.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const response = await api.participants.bulkEnroll(token, tournamentId, {
        player_profile_ids: Array.from(selected),
        division_id: divisionId ? Number(divisionId) : null,
      });
      const { added_count, skipped_existing_count, skipped_missing_count } = response.data;
      const parts = [`${added_count} ${added_count === 1 ? "jugador inscrito" : "jugadores inscritos"}.`];
      if (skipped_existing_count > 0) {
        parts.push(`${skipped_existing_count} ya ${skipped_existing_count === 1 ? "estaba inscrito" : "estaban inscritos"}.`);
      }
      if (skipped_missing_count > 0) {
        parts.push(`${skipped_missing_count} no ${skipped_missing_count === 1 ? "se encontró" : "se encontraron"}.`);
      }
      onEnrolled(parts.join(" "));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudieron inscribir los jugadores."
          : "Error de red. Intenta otra vez.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      className="insc-modal__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div
        className="insc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        aria-describedby={`${ids}-desc`}
      >
        <header className="insc-modal__head">
          <div>
            <h2 id={`${ids}-title`}>Inscribir jugadores existentes</h2>
            <p id={`${ids}-desc`}>
              Jugadores que ya están en la base del sistema. Búscalos, márcalos y elige la categoría.
            </p>
          </div>
          <button type="button" className="insc-modal__close" aria-label="Cerrar" onClick={onClose} disabled={submitting}>
            ×
          </button>
        </header>
        <div className="insc-modal__toolbar">
          {divisionRequired ? (
            <label className="insc-modal__field">
              <span>Categoría</span>
              <select value={divisionId} onChange={(event) => setDivisionId(event.target.value)} disabled={submitting}>
                <option value="">Elige categoría…</option>
                {activeDivisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="insc-modal__field insc-modal__field--grow">
            <span>Buscar jugador</span>
            <input
              ref={searchRef}
              type="search"
              value={search}
              placeholder="Nombre, correo o teléfono"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <ul className="insc-modal__list" aria-label="Jugadores del sistema" aria-busy={searching}>
          {players.length === 0 && !searching ? (
            <li className="insc-modal__empty">No encontramos jugadores con esa búsqueda.</li>
          ) : null}
          {players.map((player) => (
            <li key={player.id}>
              <label className={`insc-modal__row${selected.has(player.id) ? " is-selected" : ""}`}>
                <input
                  type="checkbox"
                  checked={selected.has(player.id)}
                  onChange={() => toggle(player.id)}
                  disabled={submitting}
                />
                <span className="insc-modal__who">
                  <strong>{player.display_name}</strong>
                  <small>{[player.email, player.phone].filter(Boolean).join(" · ") || "Sin contacto"}</small>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <footer className="insc-modal__foot">
          <Link className="insc-modal__link" to={`/admin/tournaments/${tournamentId}/setup/players`}>
            Administrar jugadores del torneo
          </Link>
          <div className="insc-modal__actions">
            <button type="button" className="insc-btn insc-btn--ghost" onClick={onClose} disabled={submitting}>
              Cerrar
            </button>
            <button
              type="button"
              className="insc-btn insc-btn--lime"
              disabled={submitting || selected.size === 0 || (divisionRequired && !divisionId)}
              onClick={() => void handleSubmit()}
            >
              {submitting ? "Inscribiendo…" : `Inscribir${selected.size > 0 ? ` (${selected.size})` : ""}`}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
